import { errAsync, ResultAsync } from "neverthrow";
import invariant from "invariant";
import { makeQueryablePromise, PromiseStatus, } from "../utils/makeQueryablePromise";
const STRIPE_SDK_URL = "https://js.stripe.com/v3";
export const STRIPE_SDK_SCRIPT_ID = "__STRIPE_SDK__";
function getScriptLoadErrorMessage(event) {
    const DEFAULT_MESSAGE = "The Stripe SDK didn't load correctly.";
    return typeof event === "string"
        ? DEFAULT_MESSAGE
        : `${DEFAULT_MESSAGE} (url: ${event.target.src})`;
}
class StripeSdk {
    constructor() {
        if (!StripeSdk.instance) {
            StripeSdk.instance = this;
        }
        return StripeSdk.instance;
    }
    get() {
        const error = this.checkState();
        if (error) {
            return errAsync(error);
        }
        return ResultAsync.fromPromise(this.loadSdk(), error => {
            if (error instanceof Error)
                return error;
            return new Error(`An unknown error occurred. Context: ${String(error)}`);
        });
    }
    clearScriptPromise() {
        this.scriptLoadPromise = undefined;
    }
    get apiKey() {
        var _a;
        const apiKey = [
            (_a = window.BASE_CONFIG) === null || _a === void 0 ? void 0 : _a.stripePubApiKey,
            process.env.STRIPE_PUB_API_KEY,
            process.env.NEXT_PUBLIC_STRIPE_PUB_API_KEY,
        ].find(Boolean);
        invariant(apiKey, "Stripe API key was not provided");
        return apiKey;
    }
    loadSdk() {
        var _a, _b;
        if (((_a = this.scriptLoadPromise) === null || _a === void 0 ? void 0 : _a.status()) === PromiseStatus.pending) {
            return this.scriptLoadPromise;
        }
        if (((_b = this.scriptLoadPromise) === null || _b === void 0 ? void 0 : _b.status()) === PromiseStatus.fulfilled) {
            return Promise.resolve(window.Stripe(this.apiKey));
        }
        this.scriptLoadPromise = makeQueryablePromise(new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = STRIPE_SDK_URL;
            script.id = STRIPE_SDK_SCRIPT_ID;
            script.onload = () => {
                resolve(window.Stripe(this.apiKey));
            };
            script.onerror = event => {
                reject(new URIError(getScriptLoadErrorMessage(event)));
            };
            if (document.body) {
                document.body.appendChild(script);
            }
        }));
        return this.scriptLoadPromise;
    }
    checkState() {
        const hasScript = document.getElementById(STRIPE_SDK_SCRIPT_ID) !== null;
        const hasPromise = Boolean(this.scriptLoadPromise);
        switch (true) {
            case hasScript && !hasPromise:
                return new Error(`The Stripe SDK has loaded, but the promise to resolve the SDK does not exist.`);
            case !hasScript && hasPromise:
                return new Error(`The Stripe SDK hasn't loaded, but the promise to resolve the SDK exists.`);
        }
    }
}
const instance = new StripeSdk();
export default instance;

// @zapier/stripe@1.0.0 (4143d282b)
